import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RecurringDocument, RecurringDocumentKind, RecurringDocumentPrefill, RecurringDocumentStatus } from '../../interfaces/recurringDocument.interface';
import { RecurringDocumentService } from '../../services/recurringDocument.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ScrollableTable from '../common/ScrollableTable';
import { formatDate } from '../../utils/dateUtils';
import { describeSchedule, statusColor } from '../accounting/recurringJournalText';
import { KIND_LABEL, modeLabel } from './recurringDocumentText';
import RecurringDocumentForm from './RecurringDocumentForm';

const th = { padding: '12px', textAlign: 'left' as const, backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' };
const td = { padding: '12px', borderBottom: '1px solid #ddd', verticalAlign: 'top' as const };

interface Props {
  /** 'sales' = invoices and quotations; 'bills' = supplier bills */
  scope: 'sales' | 'bills';
}

export const detailPath = (scope: 'sales' | 'bills', id: string) =>
  scope === 'sales' ? `/sales/recurring/${id}` : `/procurement/recurring-bills/${id}`;

const RecurringDocumentList: React.FC<Props> = ({ scope }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const institutionId = AuthService.getSelectedInstitution() ?? '';
  const canInvoice = usePermission(PERMISSIONS.SALES_INVOICE_MANAGE);
  const canQuote = usePermission(PERMISSIONS.QUOTATION_MANAGE);
  const canBill = usePermission(PERMISSIONS.SUPPLIER_BILL_CREATE);

  const kinds: RecurringDocumentKind[] = scope === 'sales' ? ['SALES_INVOICE', 'QUOTATION'] : ['SUPPLIER_BILL'];
  const creatable = kinds.filter(k => (k === 'SALES_INVOICE' ? canInvoice : k === 'QUOTATION' ? canQuote : canBill));

  const [rows, setRows] = useState<RecurringDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [formKind, setFormKind] = useState<RecurringDocumentKind | null>(null);
  const [prefill, setPrefill] = useState<RecurringDocumentPrefill | null>(null);

  const load = useCallback(async () => {
    if (!institutionId) return;
    try {
      setLoading(true);
      const all = await RecurringDocumentService.list(institutionId, undefined, (statusFilter || undefined) as RecurringDocumentStatus | undefined);
      setRows(all.filter(r => kinds.includes(r.kind)));
    } catch (e) {
      toast.showApiError(e, 'Failed to load recurring templates');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, statusFilter, scope]);

  useEffect(() => { load(); }, [load]);

  // "Make recurring" from a document: ?prefillKind=...&prefillDoc=...
  useEffect(() => {
    const k = params.get('prefillKind') as RecurringDocumentKind | null;
    const d = params.get('prefillDoc');
    if (!k || !d || !institutionId) return;
    RecurringDocumentService.prefill(institutionId, k, d)
      .then(p => { setPrefill(p); setFormKind(k); })
      .catch(e => toast.showApiError(e, 'This document cannot be made recurring'))
      .finally(() => setParams({}, { replace: true }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => rows.filter(r =>
    (!kindFilter || r.kind === kindFilter)
    && (!search.trim() || `${r.name} ${r.partyName ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()))), [rows, kindFilter, search]);

  const attention = rows.filter(r => r.hasOpenRuns || r.status === 'PAUSED').length;

  return (
    <div data-help-page="recurringDocs.list.page" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h2 style={{ margin: 0 }}>{scope === 'sales' ? 'Recurring invoices and quotations' : 'Recurring bills'}</h2>
        <Box data-help-section="recurringDocs.list.toolbar" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {creatable.map(k => (
            <Button data-help="recurringDocs.list.new" key={k} variant="contained" onClick={() => { setPrefill(null); setFormKind(k); }}>
              New recurring {KIND_LABEL[k].toLowerCase()}
            </Button>
          ))}
        </Box>
      </div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Templates create the document on a schedule through the normal create path, so every check still applies. Nothing is e-mailed to
        {scope === 'sales' ? ' customers' : ' suppliers'}. The daily job runs in the early morning, Bangladesh time.
      </Typography>
      {attention > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>{attention} template(s) are paused or have runs that need attention. Open them to retry or review.</Alert>
      )}

      <Box data-help-section="recurringDocs.list.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {scope === 'sales' && (
          <TextField data-help="recurringDocs.list.kind" select size="small" label="Kind" value={kindFilter} onChange={e => setKindFilter(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 220 }}>
            <MenuItem value="">All</MenuItem>
            {kinds.map(k => <MenuItem key={k} value={k}>{KIND_LABEL[k]}</MenuItem>)}
          </TextField>
        )}
        <TextField data-help="recurringDocs.list.status" select size="small" label="Status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 220 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="PAUSED">Paused</MenuItem>
          <MenuItem value="ENDED">Ended</MenuItem>
        </TextField>
        <TextField data-help="recurringDocs.list.search" size="small" label="Search name or party" value={search} onChange={e => setSearch(e.target.value)} sx={{ flex: '1 1 220px', maxWidth: 320 }} />
      </Box>

      {loading ? <CircularProgress /> : (
        <ScrollableTable>
          <table data-help-section="recurringDocs.list.table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th data-help="recurringDocs.list.col.name" style={th}>Name</th><th data-help="recurringDocs.list.col.kind" style={th}>Kind</th><th data-help="recurringDocs.list.col.party" style={th}>{scope === 'sales' ? 'Customer' : 'Supplier'}</th>
                <th data-help="recurringDocs.list.col.schedule" style={th}>Schedule</th><th data-help="recurringDocs.list.col.creates" style={th}>Creates</th><th data-help="recurringDocs.list.col.nextRun" style={th}>Next run</th><th data-help="recurringDocs.list.col.lastResult" style={th}>Last result</th><th data-help="recurringDocs.list.col.status" style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && <tr><td style={td} colSpan={8}>No recurring templates yet.</td></tr>}
              {visible.map(r => (
                <tr data-help="recurringDocs.list.action.open" key={r.id} style={{ cursor: 'pointer' }} onClick={() => navigate(detailPath(scope, r.id))}>
                  <td style={td}><strong>{r.name}</strong></td>
                  <td style={td}>{KIND_LABEL[r.kind]}</td>
                  <td style={td}>{r.partyName ?? '-'}</td>
                  <td style={td}>{describeSchedule(r)}</td>
                  <td style={td}>{modeLabel(r.kind, r.mode)}</td>
                  <td style={td}>{r.nextRunDate ? formatDate(r.nextRunDate) : '-'}</td>
                  <td style={td}>{r.lastResult ?? '-'}{r.hasOpenRuns && <Chip size="small" color="warning" label="Needs attention" sx={{ ml: 1 }} />}</td>
                  <td style={td}><Chip size="small" label={r.status} color={statusColor(r.status)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTable>
      )}

      {formKind && (
        <RecurringDocumentForm
          open
          kind={formKind}
          prefill={prefill}
          onClose={() => { setFormKind(null); setPrefill(null); }}
          onSaved={t => { setFormKind(null); setPrefill(null); navigate(detailPath(scope, t.id)); }}
        />
      )}
    </div>
  );
};

export default RecurringDocumentList;
